import { GridFilterItem, GridSortItem } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  EMPTY_FILTERS,
  FilterStateConversionLookups,
  cleanSavedReportFilterModel,
  filterModelToFilterState,
} from './serviceListFilters';
import { parseServiceListUrl } from './serviceListUrlState';
import { updateReportByCode } from '../../api/report';
import { FilterState } from '../../components/molecules/FilterBar';
import { SavedReport } from '../../components/molecules/ReportTabs';
import { ColumnDef } from '../../components/organisms/ServiceTable';
import { ColumnSelectionItem } from '../../components/templates/ColumnSelection';
import { SavedReportTypeList } from '../../configs/enums';
import {
  useGetSavedReports,
  useGetReportByCode,
  useGetDefaultReportByReportType,
  useAddNewReport,
  useUpdateBulkReports,
} from '../../queries/report-query';
import { generateUniqueCode } from '../../utils/common';
import { toast } from '../../utils/toast';

export interface UseReportsReturn {
  savedReports: SavedReport[];
  activeReport: string | null;
  activeReportName: string | null;
  defaultTabLabel: string;
  saveDialogOpen: boolean;
  handleReportChange: (code: string | null) => void;
  handleSaveNewReport: (name: string, setAsDefault: boolean) => Promise<void>;
  handleOverwriteReport: (setAsDefault: boolean) => Promise<void>;
  handleSetDefault: (report: SavedReport) => Promise<void>;
  setSaveDialogOpen: (open: boolean) => void;
}

interface UseReportsOptions {
  filterModel: { items: GridFilterItem[] };
  pageSize: number;
  sortModel: { field: string; sort: string | null | undefined };
  columns: ColumnDef[];
  columnSelectionData: ColumnSelectionItem[];
  applyFilters: (filters: FilterState) => void;
  applyReportConfig: (
    filterModel: { items: GridFilterItem[] },
    colSelection?: ColumnSelectionItem[],
    columnOrder?: Array<{ field: string }>
  ) => void;
  /** Restore the default view's own column layout (session, else built defaults)
   *  so returning to the default tab doesn't inherit a report's columns. */
  resetColumnsToDefault: () => void;
  setFilters: (filters: FilterState) => void;
  setFilterBarKey: React.Dispatch<React.SetStateAction<number>>;
  /**
   * The "all open statuses ticked" baseline filter from
   * useServiceListData. Used as the fallback when the user has no saved
   * default report and clicks the default tab, or when their default is
   * removed. Empty `items` while statuses are still loading; the caller's
   * own default-load effect tops up afterwards.
   */
  openStatusFilterModel: { items: GridFilterItem[] };
  openStatusFilterState: FilterState;
  /** Lookup maps for converting BFF name predicates (from saved reports) back
   *  to code/id-based FilterState values. Populated once the status and client
   *  queries resolve; empty maps fall back to name-as-is behaviour.
   *
   *  MUST be referentially stable across renders (memoised by the caller) —
   *  it sits in the apply-report effect's dependency array below. An inline
   *  object literal re-runs that effect every render, and because the effect
   *  triggers a re-render itself (setFilterBarKey), the page loops forever,
   *  hammering the BFF with one aborted /servicesList request per cycle. */
  filterStateLookups: FilterStateConversionLookups;
  /**
   * Whether the current user is a client/customer. When true, saved
   * reports' column overrides are ignored — clients see the pinned
   * client-default columns regardless of what any saved report carries.
   * Filters from saved reports still apply.
   */
  isClient?: boolean;
}

export const useReports = ({
  filterModel,
  pageSize,
  sortModel,
  columns,
  columnSelectionData,
  applyFilters,
  applyReportConfig,
  resetColumnsToDefault,
  setFilters,
  setFilterBarKey,
  openStatusFilterModel,
  openStatusFilterState,
  filterStateLookups,
  isClient = false,
}: UseReportsOptions): UseReportsReturn => {
  const location = useLocation();
  // Deep-link support: links from Reports > My Reports point at
  // `/services?savedReportCode=XYZ`. Initialise activeReport from the URL so
  // the report's filters and tab load on first render. Sync again if the URL
  // changes (e.g. back/forward navigation between report links).
  const urlSearchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const urlReportCode = useMemo(() => urlSearchParams.get('savedReportCode'), [urlSearchParams]);

  // Activity monitor, import batch, and structure deep-links set their own
  // filters via buildInitialFilterModel — the default saved report must not
  // overwrite them.
  const hasDeepLinkFilters = useMemo(
    () =>
      Boolean(
        urlSearchParams.get('importSessionId') ||
        urlSearchParams.get('structureNodeId') ||
        (urlSearchParams.get('clientCode') &&
          urlSearchParams.get('serviceTypeName') &&
          urlSearchParams.get('serviceStatusGroup'))
      ),
    [urlSearchParams]
  );

  // Codec-canonical chip URL state (e.g. `?serviceStatusName=Open`) means
  // the user is returning to a previously-applied view — typically via the
  // browser Back button after clicking into a job. Skip the default-report
  // apply so it doesn't trample the chips we just hydrated from URL.
  const hasUrlChipState = useMemo(
    () => parseServiceListUrl(urlSearchParams).hasChipState,
    [urlSearchParams]
  );

  const [activeReport, setActiveReport] = useState<string | null>(urlReportCode);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);

  useEffect(() => {
    // Adopt a code from the URL, but never clear the selection when it goes
    // away: `savedReportCode` is a transient key the URL codec strips as soon as
    // the page writes its canonical URL. Treating that removal as "no report"
    // left the report's filters applied while the tab strip snapped back to the
    // default tab.
    //
    // The alternative — keeping the code in the canonical URL and syncing both
    // ways — was rejected: the key is stripped precisely so the report does not
    // re-apply over chip state on every back-button remount (see TRANSIENT_KEYS
    // in this page's url-state module).
    //
    // The accepted trade-off is that a live component can no longer be returned
    // to the default view by the URL alone. Nothing is lost in practice: a fresh
    // mount still seeds activeReport from the URL, so back-navigation to a
    // code-less URL lands on the default, and the default tab clears the report
    // explicitly through handleReportChange(null).
    if (urlReportCode) setActiveReport(urlReportCode);
  }, [urlReportCode]);

  const addNewReport = useAddNewReport();
  const bulkUpdateReports = useUpdateBulkReports();
  const defaultReportQuery = useGetDefaultReportByReportType(SavedReportTypeList.ServiceList);

  // Fetch saved reports for the Service list
  const reportsQuery = useGetSavedReports({
    filterModel: { items: [{ field: 'pageName', operator: 'equals', value: 'ServiceList' }] },
    page: 0,
    pageSize: 100,
  });

  useEffect(() => {
    reportsQuery.refetch();
    defaultReportQuery.refetch();
  }, []);

  // Apply or reset the default report config when the query settles and
  // the user is on the default tab (no URL report, pinned report, or
  // deep-link filters active, and no codec chip state from a back-button
  // remount).
  useEffect(() => {
    if (urlReportCode || activeReport || hasDeepLinkFilters || hasUrlChipState) return;
    const defaults = defaultReportQuery.data;
    if (defaults && defaults.length > 0) {
      const def = defaults[0];
      applyReportConfig(
        def.dataGridProperties?.filterModel ?? { items: [] },
        // Clients see the pinned client-default columns regardless of any
        // saved report's columnSelection / columnOrder; only filter state
        // is honoured for them.
        isClient || !def.columnSelection?.length ? undefined : def.columnSelection,
        isClient
          ? undefined
          : (def.dataGridProperties?.columnOrder as Array<{ field: string }> | undefined)
      );
      if (def.dataGridProperties?.filterModel) {
        setFilters(
          filterModelToFilterState(
            cleanSavedReportFilterModel(def.dataGridProperties.filterModel),
            filterStateLookups
          )
        );
        setFilterBarKey((k) => k + 1);
      }
    } else if (defaults) {
      // Default was removed (or there never was one) — reset to the
      // open-statuses baseline. Skipped while statuses are still loading
      // (`openStatusFilterModel.items` is empty); useServiceListData's
      // default-load effect will top up once they resolve.
      if (openStatusFilterModel.items.length === 0) return;
      applyReportConfig(openStatusFilterModel);
      setFilters(openStatusFilterState);
      setFilterBarKey((k) => k + 1);
    }
  }, [defaultReportQuery.data, openStatusFilterModel, openStatusFilterState, isClient]);

  const defaultTabLabel = useMemo(() => {
    const defaults = defaultReportQuery.data;
    if (defaults && defaults.length > 0) return defaults[0].name;
    return 'All Open Jobs';
  }, [defaultReportQuery.data]);

  const savedReports: SavedReport[] = useMemo(() => {
    if (!reportsQuery.data?.data) return [];
    return reportsQuery.data.data.map((r) => ({
      id: r.id,
      code: r.code,
      name: r.name,
      type: (r.savedReportAccess?.length > 0 ? 'shared' : 'my') as 'my' | 'shared',
      isDefault: r.isDefault,
    }));
  }, [reportsQuery.data]);

  // Fetch and apply report config when a report tab is selected
  const reportConfigQuery = useGetReportByCode(activeReport ?? '');

  const handleReportChange = useCallback(
    (code: string | null) => {
      setActiveReport(code);
      if (code === null) {
        // Re-apply the user's default report if they have one; otherwise
        // fall back to the open-statuses baseline.
        const userDefault = defaultReportQuery.data?.[0];
        if (userDefault?.dataGridProperties) {
          applyReportConfig(
            userDefault.dataGridProperties.filterModel,
            isClient || !userDefault.columnSelection?.length
              ? undefined
              : userDefault.columnSelection,
            isClient
              ? undefined
              : (userDefault.dataGridProperties.columnOrder as Array<{ field: string }> | undefined)
          );
          const derivedFilters = filterModelToFilterState(
            cleanSavedReportFilterModel(userDefault.dataGridProperties.filterModel),
            filterStateLookups
          );
          setFilters(derivedFilters);
        } else {
          // No saved default report → apply the baseline first, THEN restore the
          // default view's own column layout LAST. applyReportConfig drops
          // user-dragged widths, so running resetColumnsToDefault after it is what
          // preserves the session widths when returning to the default tab.
          if (openStatusFilterModel.items.length > 0) {
            applyReportConfig(openStatusFilterModel);
            setFilters(openStatusFilterState);
          } else {
            // Statuses haven't loaded yet — clear to an empty filter so the
            // FilterBar reflects no ticks; the default-load effect will
            // populate the open statuses once the query resolves.
            setFilters(EMPTY_FILTERS);
            applyFilters(EMPTY_FILTERS);
          }
          resetColumnsToDefault();
        }
        setFilterBarKey((k) => k + 1);
      }
    },
    [
      applyFilters,
      applyReportConfig,
      setFilters,
      setFilterBarKey,
      defaultReportQuery.data,
      openStatusFilterModel,
      openStatusFilterState,
      filterStateLookups,
      resetColumnsToDefault,
      isClient,
    ]
  );

  useEffect(() => {
    if (activeReport) {
      reportConfigQuery.refetch();
    }
  }, [activeReport]);

  useEffect(() => {
    if (reportConfigQuery.data && activeReport) {
      const { dataGridProperties, columnSelection } = reportConfigQuery.data;
      applyReportConfig(
        dataGridProperties.filterModel,
        isClient || !columnSelection?.length ? undefined : columnSelection,
        isClient
          ? undefined
          : (dataGridProperties.columnOrder as Array<{ field: string }> | undefined)
      );
      const derivedFilters = filterModelToFilterState(
        cleanSavedReportFilterModel(dataGridProperties.filterModel),
        filterStateLookups
      );
      setFilters(derivedFilters);
      setFilterBarKey((k) => k + 1);
    }
  }, [reportConfigQuery.data, filterStateLookups]);

  const activeReportName = useMemo(
    () => savedReports.find((r) => r.code === activeReport)?.name ?? null,
    [savedReports, activeReport]
  );

  const refreshAfterDefaultChange = useCallback(() => {
    reportsQuery.refetch();
    defaultReportQuery.refetch();
  }, [reportsQuery, defaultReportQuery]);

  /**
   * Swap the isDefault flag: turn off the current default (if any) and
   * turn on the target report. Uses the bulk PATCH /defaultReport endpoint.
   */
  const setReportAsDefault = useCallback(
    async (targetId: number) => {
      const currentDefault = savedReports.find((r) => r.isDefault);
      const updates: Array<{ id: number; isDefault: boolean }> = [
        { id: targetId, isDefault: true },
      ];
      if (currentDefault && currentDefault.id !== targetId) {
        updates.unshift({ id: currentDefault.id, isDefault: false });
      }
      await bulkUpdateReports.mutateAsync(updates);
      refreshAfterDefaultChange();
    },
    [savedReports, bulkUpdateReports, refreshAfterDefaultChange]
  );

  const handleSaveNewReport = useCallback(
    async (name: string, setAsDefault: boolean) => {
      try {
        const result = await addNewReport.mutateAsync({
          name,
          code: generateUniqueCode(),
          reportTypeName: SavedReportTypeList.ServiceList,
          isDefault: setAsDefault,
          dataGridProperties: {
            filterModel,
            pageSize,
            sortModel: [sortModel as GridSortItem],
            columnOrder: columns.map((c) => ({ field: c.key })),
          },
          columnSelection: columnSelectionData,
        });
        if (setAsDefault && result?.id) {
          const currentDefault = savedReports.find((r) => r.isDefault);
          if (currentDefault) {
            await bulkUpdateReports.mutateAsync([
              { id: currentDefault.id, isDefault: false },
              { id: result.id, isDefault: true },
            ]);
          }
        }
        refreshAfterDefaultChange();
        toast.success('Report saved');
      } catch {
        toast.error('Failed to save report');
      }
    },
    [
      addNewReport,
      filterModel,
      pageSize,
      sortModel,
      columns,
      columnSelectionData,
      savedReports,
      bulkUpdateReports,
      refreshAfterDefaultChange,
    ]
  );

  const handleOverwriteReport = useCallback(
    async (setAsDefault: boolean) => {
      if (!activeReport) return;
      // savedReports comes from the paged useGetSavedReports (limit=100), so
      // a report deep-linked from outside that page won't appear there. Fall
      // back to the by-code fetch which already runs for activeReport.
      const fromList = savedReports.find((r) => r.code === activeReport);
      const fromFetch = reportConfigQuery.data;
      const target =
        fromList ??
        (fromFetch && fromFetch.code === activeReport
          ? { id: fromFetch.id, name: fromFetch.name, isDefault: fromFetch.isDefault }
          : undefined);
      if (!target) {
        toast.error('Report not found or not loaded');
        return;
      }
      try {
        // BFF treats /savedReport/{code} PUT as a full-record write — name
        // must be present or the report is rejected, so echo the existing
        // name and isDefault back in.
        await updateReportByCode(activeReport, {
          name: target.name,
          isDefault: target.isDefault,
          dataGridProperties: {
            filterModel,
            pageSize,
            sortModel: [sortModel as GridSortItem],
            columnOrder: columns.map((c) => ({ field: c.key })),
          },
          columnSelection: columnSelectionData,
        });
        if (setAsDefault && target.id != null) await setReportAsDefault(target.id);
        refreshAfterDefaultChange();
        toast.success('Report updated');
      } catch {
        toast.error('Failed to update report');
      }
    },
    [
      activeReport,
      filterModel,
      pageSize,
      sortModel,
      columns,
      columnSelectionData,
      savedReports,
      reportConfigQuery.data,
      setReportAsDefault,
      refreshAfterDefaultChange,
    ]
  );

  const handleSetDefault = useCallback(
    async (report: SavedReport) => {
      try {
        if (report.isDefault === false) {
          await bulkUpdateReports.mutateAsync([{ id: report.id, isDefault: false }]);
          refreshAfterDefaultChange();
          toast.success(`"${report.name}" removed as default`);
        } else {
          await setReportAsDefault(report.id);
          toast.success(`"${report.name}" set as default`);
        }
      } catch {
        toast.error('Failed to update default report');
      }
    },
    [setReportAsDefault, bulkUpdateReports, refreshAfterDefaultChange]
  );

  return {
    savedReports,
    activeReport,
    activeReportName,
    defaultTabLabel,
    saveDialogOpen,
    handleReportChange,
    handleSaveNewReport,
    handleOverwriteReport,
    handleSetDefault,
    setSaveDialogOpen,
  };
};
